import React, {useEffect} from 'react';
import { useMemo, useState } from "react";
import Navbar from "./Navbar";
import Portfolio from "./Portfolio";
import Markets from "./Markets";
import Order from "./Order";
import {
    addEvent,
    addStrategy,
    buyTicker,
    initialMarket,
    sellTicker,
    updateEvents,
    updateTicker
} from "../slices/marketSlice";
import { useAppDispatch, useAppSelector } from "../hooks";
import TickerClock from "../libs/TickerClock";
import News from "./News";
import EventGenerator from "../libs/EventGenerator";
import {events} from "../stores/events";
import TimerClock from "../libs/TimerClock";
import { createClient } from "@supabase/supabase-js";
import Leaderboards from "./Leaderboards";
import Progress from "./Progress";
import {Charts} from "./Charts";
import Stratiegies from "./Strategies";
import TickerDetail from "./TickerDetail";
import Footer from "./Footer";


const supabase = createClient(
    // @ts-ignore
    process.env.REACT_APP_SUPABASE_URL,
    process.env.REACT_APP_SUPABASE_ANON_KEY
);


export default function Game(props: {user: string|undefined}) {

    const market = useAppSelector((state) => state.market);
    const dispatch = useAppDispatch();

    const eventGenerator = new EventGenerator(market.seed, events);

    const [selectedTicker, setSelectedTicker] = useState('MSLA');
    const onTickerSelect = (ticker: {symbol: string, value: number, key: string}) => setSelectedTicker(ticker.key);

    const marketValue = useMemo(() => {

        let currentMarketValue = market.cash;

        Object.entries(market.portfolio).forEach(([symbol, ticker]) => {
            const fractions = ticker.fractions || 0;
            const currentTickerValue = market.tickers[symbol]?.value || 0;
            currentMarketValue += currentTickerValue * fractions;
        })

        return currentMarketValue;

    }, [market.cash, market.portfolio, market.tickers]);

    const onTickerBuy = (ticker: {symbol: string, amount: number}) => dispatch(buyTicker({ticker: ticker}));

    const onTickerSell = (ticker: {symbol: string, amount: number}) => dispatch(sellTicker({ticker: ticker}));

    const onTick = () => {

        setProgress(progress => progress + 1);

        // Update events duration:
        dispatch(updateEvents());

        // Possibly generate a new event:
        const randomEvent = eventGenerator.nextOrNull();
        if (randomEvent) dispatch(addEvent({event: randomEvent}));

        // Update tickers prices:
        Object.keys(market.tickers).forEach((symbol) => {

            dispatch(updateTicker({ticker: {symbol: symbol}}));

        });

    }

    const TICK_DURATION = 800;

    const clock = new TickerClock(TICK_DURATION, onTick);

    const [subscribed, setSubscribed] = useState(false);

    const [scores, setScores] = useState<any[]>([]);

    const channel = useMemo(() => {
        return supabase.channel(`game-${market.seed}`, {config: {presence: { key: props.user }}})
            .on('presence', { event: 'sync' }, () => {
                const state = channel.presenceState();
                const latestScores: any[] = [];
                Object.keys(state).forEach((username) => {
                    latestScores.push(state[username][state[username].length - 1]);
                });
                latestScores.sort((score1, score2) => score2.score - score1.score);
                setScores(latestScores);
            })
            // @ts-ignore
            .subscribe(async (status) => {
                if (status === 'SUBSCRIBED') setSubscribed(true);
            })
    }, [market.seed, props.user]);

    useEffect(() => {
        if (!subscribed) return;
        channel.track({ username: props.user, score: marketValue }).then(() => {});
    }, [subscribed, marketValue, props.user, channel]);

    const onTimeout = () => {
        clock.stop();
        window.alert(`Market has closed!`);
        setShowLeaderboards(true);
    };

    const GAME_DURATION = 3 * 60;

    const timer = new TimerClock(GAME_DURATION, onTimeout);

    const onStartClick = () => {

        window.alert(`Share this URL to play with your friends: ${process.env.REACT_APP_BASE_URL}/${market.seed}`);

        clock.start();
        timer.start();

    }

    const [showLeaderboards, setShowLeaderboards] = useState(false);

    const [progress, setProgress] = useState(0);

    const onStrategyBuy = (index: number) => {
        dispatch(addStrategy({strategy: {key: index}}));
    }

    return (
        <div className="h-screen w-screen grid place-items-center">
            <div className="w-screen max-w-3xl">
                <Navbar onStartClick={onStartClick} user={props.user} />
                <Progress progress={ progress * TICK_DURATION / (GAME_DURATION * 1000) * 100 } />
                <Portfolio marketValue={marketValue} cash={market.cash} portfolio={market.portfolio} />
                <Markets currentTickers={market.tickers} initialTickers={initialMarket.tickers} selected={selectedTicker} onTickerSelect={onTickerSelect} />
            </div>
            <div className="w-screen max-w-lg">
                <div className="flex items-center relative h-80 w-full my-4">
                    <TickerDetail symbol={selectedTicker} currentTicker={market.tickers[selectedTicker]} initialTicker={initialMarket.tickers[selectedTicker]} />
                    <Charts history={market.history} selected={selectedTicker} />
                    <Order selectedTicker={selectedTicker} onBuyClick={onTickerBuy} onSellClick={onTickerSell} />
                </div>
                <Leaderboards show={showLeaderboards} scores={scores} />
                <News news={market.news} />
                <Stratiegies strategies={market.strategies} cash={market.cash} onStrategyBuy={onStrategyBuy} />
            </div>
            <Footer />
        </div>
    );
}